import React, { SyntheticEvent, useRef, useState } from 'react';
import { SelectFieldType, SelectOption } from '@/type';
import { buildFormFieldLabel } from '@/util';
import {
  Autocomplete,
  Chip,
  FormControl,
  FormHelperText,
  MenuItem,
  Popover,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StringSchema } from 'yup';
import TextField from '@mui/material/TextField';
import SelectInputGetTooltipContent from '@components/common/input/SelectInputGetTooltipContent';
import { TEXT_STYLES } from '@/constant';
import { Box } from '@mui/system';
import { useUpgradeTagLimit } from '@components/common/hooks/selectInput.hooks';

interface SelectInputProps {
  field: any;
  selectField: SelectFieldType;
  error?: any;
}

export const buildArray = (
  value: string | string[] | number | number[],
): string[] | number[] =>
  value
    ? Array.isArray(value)
      ? (value as string[]).filter(Boolean)
      : typeof value === 'string'
        ? value.split(',').map((item) => item.trim())
        : [value]
    : [];

const SelectInput = ({ field, selectField, error }: SelectInputProps) => {
  const { t } = useTranslation();
  const selectLabelId = `generic-select-${selectField.name}`;
  const AVERAGE_TAG_WIDTH = 120;
  const TAG_PADDING = 1;
  const autocompleteRef = useRef<HTMLElement>(null);
  const tagLimit = useUpgradeTagLimit(autocompleteRef, AVERAGE_TAG_WIDTH, TAG_PADDING);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handlePopoverClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);

  const getDefaultValues = (): string | string[] | number | number[] => {
    if (selectField.isMultiselect) {
      return buildArray(selectField.defaultValues ?? []);
    }
    return selectField.defaultValue ?? '';
  };

  const mapSelectInputValue = (value: any) => {
    if (selectField.isMultiselect) {
      return (value || []).map(
        (val) =>
          selectField.options?.find((option) => option.value === val) || {
            value: val,
            label: String(val),
          },
      );
    }
    return selectField.options?.find((opt) => opt.value === value);
  };

  const buildLabel = () =>
    buildFormFieldLabel(t(selectField.label), selectField.validation as StringSchema);

  return (
    <FormControl fullWidth required={selectField.required} error={!!error}>
      <Autocomplete
        {...field}
        id={selectLabelId}
        limitTags={tagLimit}
        ref={autocompleteRef}
        options={selectField?.options || []}
        disabled={selectField.disabled}
        defaultValue={getDefaultValues()}
        multiple={selectField.isMultiselect}
        value={mapSelectInputValue(field.value)}
        noOptionsText={t('common.no_available_data')}
        disableCloseOnSelect={selectField.isMultiselect}
        clearOnBlur={false}
        getOptionLabel={(option: SelectOption) => option.label ?? ''}
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'preventOverflow',
                options: {
                  boundary: 'window',
                },
              },
            ],
          },
          paper: { sx: { backgroundColor: 'var(--main-color) !important' } },
          listbox: { sx: { backgroundColor: 'var(--main-color) !important' } },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            error={!!error}
            label={buildLabel()}
            InputLabelProps={{
              shrink: true,
            }}
            sx={selectInputTextFieldStyle}
          />
        )}
        renderOption={(props, option: SelectOption) => (
          <MenuItem
            {...props}
            key={option.value}
            sx={selectInputMenuItemStyle(field, option)}
          >
            {option.label}
          </MenuItem>
        )}
        renderTags={(tagValue, getTagProps) => {
          const displayTags = tagValue.slice(0, tagLimit);
          const extraCount = tagValue?.length - tagLimit;

          return (
            <>
              {displayTags.map((option: SelectOption) => (
                <Chip
                  disabled={selectField.disabled}
                  {...getTagProps}
                  key={option.label}
                  label={option.label}
                  onDelete={() => {
                    const updatedValue = field.value.filter(
                      (value) => value != option.value,
                    );
                    field.onChange(updatedValue);
                    selectField.onChange?.(updatedValue);
                  }}
                />
              ))}
              {extraCount > 0 && (
                <Box
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                >
                  +{extraCount}
                </Box>
              )}
            </>
          );
        }}
        onChange={(_: SyntheticEvent, option: SelectOption) => {
          const normalizedValue = Array.isArray(option)
            ? option.map((option) => option.value)
            : option?.value;
          field.onChange(normalizedValue);
          selectField.onChange?.(normalizedValue);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            field.onBlur();
            selectField?.onBlur?.(field.name);
          }
        }}
        onBlur={() => {
          field.onBlur();
          selectField?.onBlur?.(field.name);
        }}
        aria-owns={open ? 'autocomplete-popover' : undefined}
        aria-haspopup='true'
        onMouseOver={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
      />
      <Popover
        id='autocomplete-popover'
        sx={{
          pointerEvents: 'none',
        }}
        open={open && selectField.isMultiselect && field.value?.length > tagLimit}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
        disableEnforceFocus
      >
        <SelectInputGetTooltipContent selected={field.value} selectField={selectField} />
      </Popover>
      {error && <FormHelperText>{t(error.message || '')}</FormHelperText>}
    </FormControl>
  );
};

const selectInputMenuItemStyle = (field: any, option: SelectOption) => ({
  color:
    Array.isArray(field.value) && field.value.includes(option.value)
      ? TEXT_STYLES.COLORS.TEXT_PRIMARY
      : TEXT_STYLES.COLORS.TEXT_LIGHT,
  borderBottom: '1px solid #26586B !important',
  '&:hover': {
    backgroundColor: 'var(--main-color-30)',
    color: TEXT_STYLES.COLORS.TEXT_PRIMARY_HOVER,
  },
  '&:last-of-type': { borderBottom: 'none !important' },
});

export const selectInputTextFieldStyle = {
  '.MuiAutocomplete-popupIndicator': {
    backgroundColor: 'transparent',
    border: 'none',
  },
  '.MuiAutocomplete-clearIndicator': {
    display: 'none',
  },
  width: '100%',
  maxWidth: '100%',
  minWidth: '100%',
};

export default SelectInput;
